import { createContext, useContext, type ReactElement, type ReactNode } from 'react'
import type { FloatProps } from '@headlessui-float/react'
import { Float } from '@headlessui-float/react'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import { useHeadlessUiAnchor } from 'shared/feature-flags/use-headless-ui-anchor'

type FloatPlacement = NonNullable<FloatProps['placement']>

interface HoverOverlayProps {
  show: boolean
  placement?: FloatPlacement
  portal?: boolean
  gap?: number
  className?: string
  children: [ReactElement, ReactElement]
  floatProps?: Omit<FloatProps, 'children' | 'className' | 'placement' | 'portal' | 'show'>
}

const HoverOverlayContext = createContext(false)

type AnchorTo =
  | 'bottom end'
  | 'bottom start'
  | 'bottom'
  | 'left end'
  | 'left start'
  | 'left'
  | 'right end'
  | 'right start'
  | 'right'
  | 'top end'
  | 'top start'
  | 'top'

export const toHeadlessAnchor = (placement: string): AnchorTo =>
  placement.replace('-', ' ') as AnchorTo

export const HoverOverlayArrow = ({ className }: { className?: string }): ReactNode => {
  const useNativeAnchor = useContext(HoverOverlayContext)

  if (useNativeAnchor) {
    return <div className={className} />
  }

  return <Float.Arrow className={className} />
}

const resolveNativeAnchor = (
  placement: string,
  gap: number,
  offset: FloatProps['offset'],
): { to: AnchorTo; gap: number; offset?: number } => {
  if (typeof offset === 'object' && offset?.crossAxis != null) {
    return { to: toHeadlessAnchor(placement), gap, offset: offset.crossAxis }
  }

  return { to: toHeadlessAnchor(placement), gap }
}

export const HoverOverlay = ({
  show,
  placement = 'bottom',
  portal = false,
  gap = 8,
  className,
  children,
  floatProps,
}: HoverOverlayProps): ReactNode => {
  const useNativeAnchor = useHeadlessUiAnchor()
  const [reference, panel] = children

  if (useNativeAnchor) {
    return (
      <HoverOverlayContext.Provider value={true}>
        <Popover className={className}>
          <PopoverButton as="div" className="inline-flex h-full items-center">
            {reference}
          </PopoverButton>
          {show ? (
            <PopoverPanel
              static
              portal={portal}
              anchor={resolveNativeAnchor(placement, gap, floatProps?.offset)}
              className="z-50"
            >
              {panel}
            </PopoverPanel>
          ) : null}
        </Popover>
      </HoverOverlayContext.Provider>
    )
  }

  return (
    <HoverOverlayContext.Provider value={false}>
      <Float
        show={show}
        placement={placement}
        portal={portal}
        className={className}
        {...floatProps}
      >
        {reference}
        {panel}
      </Float>
    </HoverOverlayContext.Provider>
  )
}
